import { useEffect, useState } from 'react';

interface ScrollCursorOptions {
  itemSelector: string;
  revision?: unknown;
  defaultRatio?: number;
}

interface ScrollCursor {
  activeId: number | null;
  setActiveId: (id: number | null) => void;
  focusRatio: number;
  focusAt: (ratio: number) => void;
}

export function useScrollCursor({
  itemSelector,
  revision,
  defaultRatio = 0.55,
}: ScrollCursorOptions): ScrollCursor {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [focusRatio, setFocusRatio] = useState(defaultRatio);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const top = Math.round(focusRatio * 100);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = Number((entry.target as HTMLElement).dataset.cursorId);
          if (id) setActiveId(id);
        }
      },
      { rootMargin: `-${top}% 0px -${100 - top}% 0px`, threshold: 0 },
    );
    document.querySelectorAll(itemSelector).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [focusRatio, itemSelector, revision]);

  return { activeId, setActiveId, focusRatio, focusAt: setFocusRatio };
}
